import supabase from './Supabase';

// Featured scripts are the one part of the library readable without an account.
// They're served entirely by the featured-script edge function (service role,
// allowlist-checked), so nothing here goes through the anon Postgres client —
// anon has no select on "files" and that stays true.

export interface FeaturedScript {
    slug: string;
    headline: string;
    blurb: string | null;
    allowDownload: boolean;
    /** True while the script is staged for approver preview only. */
    adminOnly: boolean;
    featuredFrom: string;
    scriptTitle: string | null;
    writers: string | null;
    draft: string | null;
    draftDate: string | null;
    documentType: string | null;
    episode: string | null;
    alternateTitles: string | null;
    industryList: string | null;
    year: string | null;
    isPdf: boolean;
}

export interface FeaturedScriptDetail extends FeaturedScript {
    /** Short-lived signed URL for the PDF preview. */
    url: string;
}

export type FeaturedError = 'notFound' | 'rateLimited' | 'unavailable';

const FUNCTION_BASE = `${process.env.REACT_APP_SUPABASE_ENDPOINT}/functions/v1/featured-script`;

function resolveUrl(pathOrUrl: string): string {
    try {
        return new URL(pathOrUrl, process.env.REACT_APP_SUPABASE_ENDPOINT).toString();
    } catch {
        return pathOrUrl;
    }
}

async function callFeatured(path: string): Promise<{ data: any; error: FeaturedError | null }> {
    try {
        const headers: Record<string, string> = {
            // The gateway still requires a key even though the function itself
            // takes no user identity; the anon key grants nothing on its own here.
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        };

        // Forwarded separately from the gateway's anon Bearer. It only ever adds
        // access: approvers and feature admins can see scripts still staged
        // behind admin_only.
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
            headers['x-user-token'] = session.access_token;
        }

        const response = await fetch(`${FUNCTION_BASE}${path}`, { headers });

        if (response.status === 404 || response.status === 403) {
            return { data: null, error: 'notFound' };
        }

        if (response.status === 429) {
            return { data: null, error: 'rateLimited' };
        }

        if (!response.ok) {
            return { data: null, error: 'unavailable' };
        }

        return { data: await response.json(), error: null };
    } catch (error) {
        console.error('Error calling featured-script function:', error);
        return { data: null, error: 'unavailable' };
    }
}

export async function getFeaturedScripts(): Promise<{ scripts: FeaturedScript[]; error: FeaturedError | null }> {
    const { data, error } = await callFeatured('');
    if (error) return { scripts: [], error };
    return { scripts: (data?.scripts ?? []) as FeaturedScript[], error: null };
}

/**
 * Look up one featured script by slug (or file_id) and get a signed preview URL.
 * Returns an error code rather than throwing so the page can tell "this script
 * isn't featured" apart from "try again in a minute".
 */
export async function getFeaturedScript(
    identifier: string
): Promise<{ script: FeaturedScriptDetail | null; error: FeaturedError | null }> {
    const { data, error } = await callFeatured(`/${encodeURIComponent(identifier)}`);
    if (error || !data) return { script: null, error: error ?? 'unavailable' };

    return { script: { ...data, url: resolveUrl(data.url) } as FeaturedScriptDetail, error: null };
}

export async function getFeaturedDownloadURL(identifier: string): Promise<string | null> {
    const { data, error } = await callFeatured(`/${encodeURIComponent(identifier)}/download`);
    if (error || !data?.url) return null;
    return resolveUrl(data.url);
}

// --- Admin curation (approvers and feature admins, enforced by RLS on
// featured_scripts) -------------------------------------------------------

export interface FeaturedScriptRow {
    file_id: string;
    slug: string;
    headline: string | null;
    blurb: string | null;
    allow_download: boolean;
    admin_only: boolean;
    is_active: boolean;
    featured_from: string;
    featured_until: string | null;
}

export async function getFeaturedAdminList(): Promise<(FeaturedScriptRow & { scriptTitle: string | null })[]> {
    const { data, error } = await supabase
        .from('featured_scripts')
        .select('*, files(file_id, "Script Title")')
        .order('featured_from', { ascending: false });

    if (error) {
        console.error('Error loading featured scripts:', error);
        return [];
    }

    return (data ?? []).map((row: any) => ({
        ...row,
        scriptTitle: row.files?.['Script Title'] ?? null,
    }));
}

export async function upsertFeaturedScript(row: Partial<FeaturedScriptRow> & { file_id: string; slug: string }) {
    const { error } = await supabase.from('featured_scripts').upsert(row, { onConflict: 'file_id' });

    if (error) {
        console.error('Error saving featured script:', error);
        return error.message;
    }

    return null;
}

export async function updateFeaturedScript(file_id: string, changes: Partial<FeaturedScriptRow>) {
    const { error } = await supabase.from('featured_scripts').update(changes).eq('file_id', file_id);

    if (error) {
        console.error('Error updating featured script:', error);
        return error.message;
    }

    return null;
}

export async function removeFeaturedScript(file_id: string) {
    const { error } = await supabase.from('featured_scripts').delete().eq('file_id', file_id);

    if (error) {
        console.error('Error removing featured script:', error);
        return false;
    }

    return true;
}

/** Turn a script title into the default URL slug, e.g. "Gone Girl" -> "gone-girl". */
export function slugify(value: string): string {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80)
        .replace(/-+$/g, '');
}
